import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faHeart, faTag, faCog, faQuestionCircle, faShieldAlt, faFileContract, faSignOutAlt, faInfoCircle, faHeadset } from '@fortawesome/free-solid-svg-icons';
import MobileLayout from '../../components/Mobile/MobileLayout';
import { useAuth } from '../../context/AuthContext';

const MobileMenu: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useAuth();

  const menuSections = [
    {
      title: 'ACCOUNT',
      items: isAuthenticated
        ? [
            {
              icon: faUser,
              label: 'My Profile',
              action: () => navigate('/dashboard?tab=profile'),
              color: 'blue',
            },
            {
              icon: faTag,
              label: 'My Ads',
              action: () => navigate('/dashboard?tab=ads'),
              color: 'green',
            },
            {
              icon: faHeart,
              label: 'Favorites',
              action: () => navigate('/dashboard?tab=favorites'),
              color: 'red',
            },
          ]
        : [
            {
              icon: faUser,
              label: 'Sign In',
              action: () => navigate('/login'),
              color: 'blue',
            },
            {
              icon: faUser,
              label: 'Create Account',
              action: () => navigate('/register'),
              color: 'green',
            },
          ],
    },
    {
      title: 'SUPPORT',
      items: [
        {
          icon: faHeadset,
          label: 'Help Center',
          action: () => navigate('/help'),
          color: 'purple',
        },
        {
          icon: faQuestionCircle,
          label: 'FAQ',
          action: () => navigate('/faq'),
          color: 'blue',
        },
        {
          icon: faShieldAlt,
          label: 'Safety Tips',
          action: () => navigate('/safety'),
          color: 'green',
        },
      ],
    },
    {
      title: 'LEGAL',
      items: [
        {
          icon: faFileContract,
          label: 'Terms of Service',
          action: () => navigate('/terms'),
          color: 'gray',
        },
        {
          icon: faShieldAlt,
          label: 'Privacy Policy',
          action: () => navigate('/privacy'),
          color: 'gray',
        },
      ],
    },
    {
      title: 'OTHER',
      items: [
        {
          icon: faInfoCircle,
          label: 'About Us',
          action: () => navigate('/about'),
          color: 'blue',
        },
        {
          icon: faCog,
          label: 'Settings',
          action: () => navigate('/settings'),
          color: 'gray',
        },
        ...(isAuthenticated
          ? [
              {
                icon: faSignOutAlt,
                label: 'Logout',
                action: () => {
                  logout();
                  navigate('/');
                },
                color: 'red',
              },
            ]
          : []),
      ],
    },
  ];

  const getColorClasses = (color: string) => {
    const colors: any = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      red: 'bg-red-100 text-red-600',
      purple: 'bg-purple-100 text-purple-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      gray: 'bg-gray-100 text-gray-600',
    };
    return colors[color] || colors.blue;
  };

  return (
    <MobileLayout>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-6 text-white">
        <h1 className="text-2xl font-bold">Menu</h1>
        {isAuthenticated && user ? (
          <p className="text-blue-100 text-sm mt-1">Hi, {user.displayName || user.username}!</p>
        ) : (
          <p className="text-blue-100 text-sm mt-1">Quick access to all features</p>
        )}
      </div>

      {/* Menu Sections */}
      <div className="p-4 space-y-6">
        {menuSections.map((section, sectionIndex) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: sectionIndex * 0.1 }}
          >
            <h2 className="text-xs font-semibold text-gray-500 mb-2 px-1">
              {section.title}
            </h2>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {section.items.map((item: any, itemIndex: number) => (
                <motion.button
                  key={item.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: sectionIndex * 0.1 + itemIndex * 0.05 }}
                  onClick={item.action}
                  className="w-full flex items-center gap-4 p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                >
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getColorClasses(item.color)}`}>
                    <FontAwesomeIcon icon={item.icon} className="text-sm" />
                  </div>

                  {/* Label */}
                  <span className="flex-1 text-left text-sm font-medium text-gray-900">
                    {item.label}
                  </span>

                  {/* Arrow */}
                  <FontAwesomeIcon
                    icon={'faChevronRight' as any}
                    className="text-gray-400 text-xs"
                  />
                </motion.button>
              ))}
            </div>
          </motion.div>
        ))}

        {/* App Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center py-6"
        >
          <p className="text-xs text-gray-400">NayaBajar v1.0.0</p>
          <p className="text-xs text-gray-400 mt-1">Made with ❤️ in Nepal</p>
        </motion.div>
      </div>
    </MobileLayout>
  );
};

export default MobileMenu;
